import Modal from 'react-bootstrap/Modal'
import { Badge, Button, Spinner } from 'react-bootstrap'
import { useRef, useState } from 'react'
import generatePDF from 'react-to-pdf'
import { t } from '../../locale'
import RenderObject from '../KRenderKyc'
import { zefixCompanyDetails } from '../../service/zefix'
import { ZefixDetailsPdf, ZefixResultModalProps, ZefixSearchResult } from '../../types/zefix.type'
import { ZefixPdfContent } from './ZefixPdfContent'
import { statusLabel, statusVariant } from '../../helpers/zefix.helper'

const ZEFIX_VISIBLE_FIELDS = [
  'name',
  'uid',
  'legalForm',
  'canton',
  'status',
  'legalSeatId',
  'sogcPub',
  'zefixDetailWeb',
]

function ZefixResultModal({
  show,
  results,
  client,
  clientId,
  initialDetails = null,
  onClose,
}: ZefixResultModalProps) {
  const items = results as ZefixSearchResult[]
  const [details, setDetails] = useState<Record<string, unknown> | null>(initialDetails)
  const [loading, setLoading] = useState(false)
  const pdfRef = useRef<HTMLDivElement>(null)
  const MAX_UPLOAD_SIZE_BYTES = 25 * 1024 * 1024

  const handleSelect = async (uid: string | null) => {
    if (!uid) return
    try {
      setLoading(true)
      const data = await zefixCompanyDetails(uid, clientId)
      setDetails(data)
    } catch (err) {
      console.error('Fehler beim Laden der Firmendetails:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setDetails(null)
    onClose()
  }

  const handleDownloadPdf = async () => {
    const pdfData = details as ZefixDetailsPdf

    if (!clientId) {
      alert(t('setClient') || 'Client is required')
      return
    }

    const baseName = (pdfData?.name || 'handelsregister').replace(/[\\/:*?"<>|]/g, '_')
    const filename = `${baseName}.pdf`

    let pdfBlob: Blob
    try {
      const pdf = await generatePDF(pdfRef, { filename, page: { margin: 0, format: 'A4' } })
      pdfBlob = pdf.output('blob')

      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Generated PDF is empty')
      }

      if (pdfBlob.size > MAX_UPLOAD_SIZE_BYTES) {
        alert(t('errorUploadingFile') || 'File is too large to upload')
        return
      }
    } catch (err) {
      console.error('Error generating PDF:', err)
      alert(t('errorGeneratingPdf') || 'Error generating PDF')
      return
    }

    // Upload the same PDF bytes to backend filesystem
    const formData = new FormData()
    formData.append('clientId', clientId || '')
    formData.append('type', 'zefix')
    formData.append('firmename', client?.name || 'unknown')
    formData.append('file', pdfBlob, filename)

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/files/kyc/upload`, {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) {
        const responseText = await res.text()
        throw new Error(`Upload failed with status ${res.status}. Response: ${responseText}`)
      }
      await res.json()
    } catch (err) {
      console.error('Error uploading file:', err)
      alert(t('errorUploadingFile') || 'Error uploading file to server')
    }
  }

  return (
    <>
      {details && <ZefixPdfContent data={details as ZefixDetailsPdf} pdfRef={pdfRef} />}

      <Modal show={show} centered size='lg' onHide={handleClose}>
        <Modal.Header className='bg-secondary text-white' closeButton>
          <Modal.Title className='fs-5'>{t('zefixCheckResults')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='zefix-modal__body'>
          {loading && (
            <div className='d-flex justify-content-center py-4'>
              <Spinner animation='border' variant='primary' />
            </div>
          )}

          {!loading && details && (
            <div className='zefix-modal__details'>
              <RenderObject data={details} allowedKeys={ZEFIX_VISIBLE_FIELDS} />
            </div>
          )}

          {!loading &&
            !details &&
            (items.length === 0 ? (
              <p className='text-muted text-center py-3'>{t('noResults')}</p>
            ) : (
              items.map((item, index) => (
                <div key={index} className='zefix-modal__result-card'>
                  <div className='zefix-modal__result-header d-flex justify-content-between align-items-center px-3 py-2'>
                    <strong className='fs-6'>{item.name}</strong>
                    <div className='d-flex align-items-center gap-2'>
                      <Badge bg={statusVariant(item.status)}>{statusLabel(item.status)}</Badge>
                      <Button
                        variant='primary'
                        size='sm'
                        disabled={!item.uid}
                        onClick={() => handleSelect(item.uid)}
                      >
                        {t('zefixSelectCompany')}
                      </Button>
                    </div>
                  </div>
                  <div className='zefix-modal__result-body px-3 py-2'>
                    <RenderObject data={item} allowedKeys={ZEFIX_VISIBLE_FIELDS} />
                  </div>
                </div>
              ))
            ))}
        </Modal.Body>
        <Modal.Footer>
          {details && (
            <Button variant='outline-dark' size='sm' onClick={handleDownloadPdf}>
              <i className='bi bi-file-earmark-pdf me-1' />
              {t('downloadPdf')}
            </Button>
          )}
          <Button variant='secondary' onClick={handleClose}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ZefixResultModal
